import { Component, inject, signal } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Socket } from 'ngx-socket-io';
import { MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { TAGInvItemType, TAGInvTransaction } from '../../../../models/taginventory';
import { ApiService } from '../../../api.service';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { dialogConfig } from '../../../dialogs';
import { PrinterService } from '../../../printer.service';
import { TransactionEditDialog, TransactionEditDialogData } from '../transaction-edit-dialog/transactionEditDialog';

export interface MissingItemsDialogData {
  socket: Socket;
}

export interface MissingItem {
  item: TAGInvItemType;
  instanceID: string;
  transaction: TAGInvTransaction;
}

@Component({
  selector: 'app-internal-missing-items-dialog',
  imports: [
    SkeletonModule,
    DialogModule,
    ButtonModule,
    TranslatePipe,
    FormsModule,
    MatIcon,
    CheckboxModule,
    EmptyStateComponent,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './missingItemsDialog.html',
  styleUrl: './missingItemsDialog.scss',
})
export class MissingItemsDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  public api = inject(ApiService);
  public printer = inject(PrinterService);

  transactions = signal<TAGInvTransaction[]>([]);
  loadingTransactions = signal(true);
  itemTypes = signal<TAGInvItemType[]>([]);
  itemTypesCount = signal<Record<string, number>>({});

  missingItems = signal<MissingItem[]>([]);
  selectedItems = signal<MissingItem[]>([]);

  expandedTypes = signal<Record<string, boolean>>({});

  constructor(
    public dialogRef: DynamicDialogRef<MissingItemsDialog>,
    public config: DynamicDialogConfig<MissingItemsDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.missingItems.title')();
  }

  toggleCollapse(typeId: string): void {
    this.expandedTypes.update((ct) => {
      ct[typeId] = !ct[typeId];
      return Object.assign({}, ct);
    });
  }

  itemFilter(itemid: string) {
    return this.missingItems().filter((m) => m.item._id == itemid);
  }

  instanceSelected(itemID: string, instanceID: string, checked: boolean) {
    var missingItem = this.missingItems().find(
      (missingItem) => missingItem.item._id == itemID && missingItem.instanceID == instanceID,
    );
    if (checked) {
      this.selectedItems.update((si) => {
        si.push(missingItem);
        return [...si];
      });
    } else {
      this.selectedItems.update((si) => {
        return si.filter(
          (selectedItem) =>
            selectedItem.item._id != missingItem.item._id ||
            selectedItem.instanceID != missingItem.instanceID,
        );
      });
    }
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.api
        .getInvItems()
        .then((invItems: TAGInvItemType[]) => {
          this.api
            .getInvTransactions()
            .then((invTransactions: TAGInvTransaction[]) => {
              this.transactions.set(invTransactions);

              for (let item of invItems.filter((item) => !item.deleted)) {
                for (let instance of item.instances.filter((instance) => !instance.deleted)) {
                  if (!this.instanceStatus(item._id, instance.itemid)) {
                    this.missingItems.update((mi) => {
                      mi.push({
                        item: item,
                        instanceID: instance.itemid,
                        transaction: this.getOpenTransaction(item._id, instance.itemid),
                      });
                      return [...mi];
                    });
                    if (!this.itemTypes().includes(item))
                      this.itemTypes.update((it) => {
                        it.push(item);
                        return [...it];
                      });
                  }
                }
              }

              this.itemTypesCount.set(
                this.itemTypes().reduce(
                  (acc, item) => {
                    const count = this.missingItems().filter(
                      (itm) => itm.item._id === item._id,
                    ).length;
                    acc[item._id] = count;
                    return acc;
                  },
                  {} as Record<string, number>,
                ),
              );

              this.loadingTransactions.set(false);
              this.initSocketHandler();
            })
            .catch((reason) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
                detail: reason.message,
              });
            });
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotLoadInventoryItems')(),
            detail: reason.message,
          });
        });
    });
  }

  ngOnDestroy() {
    this.config.data.socket.removeListener(
      'updateTransaction',
      this.socket_onUpdateTransaction.bind(this),
    );
    this.config.data.socket.removeListener(
      'createTransaction',
      this.socket_onCreateTransaction.bind(this),
    );
  }

  initSocketHandler() {
    this.config.data.socket.on('updateTransaction', this.socket_onUpdateTransaction.bind(this));
    this.config.data.socket.on('createTransaction', this.socket_onCreateTransaction.bind(this));
  }

  socket_onUpdateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.splice(
        t.findIndex((t) => t._id == newTrans._id),
        1,
        newTrans,
      );
      return [...t];
    });
  }

  socket_onCreateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.unshift(newTrans);
      t.sort((a, b) => {
        return a.startdate > b.startdate ? -1 : 1;
      });
      return [...t];
    });
  }

  getOpenTransaction(itemID: string, instanceID: string) {
    var filtered = this.transactions().filter(
      (transaction) => transaction.findItem(itemID, instanceID) != null,
    );
    return filtered.find((transaction) => transaction.findItem(itemID, instanceID).back == null);
  }

  instanceStatus(itemID: string, instanceID: string) {
    var transaction = this.getOpenTransaction(itemID, instanceID);
    return transaction == null;
  }

  showTransaction(transaction: TAGInvTransaction) {
    const transactionEditRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transaction._id,
          socket: this.config.data.socket,
        },
      }),
    );

    this.dialogRef.close();
  }

  printLabelsAll() {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          for (let selItem of this.selectedItems()) {
            this.printer.printItemLabels(selItem.item, [
              selItem.item.instances.find((instance) => instance.itemid == selItem.instanceID),
            ]);
          }
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else
      for (let selItem of this.selectedItems()) {
        this.printer.printItemLabels(selItem.item, [
          selItem.item.instances.find((instance) => instance.itemid == selItem.instanceID),
        ]);
      }
  }
}
